import { gql, useLazyQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal';
import { SmallSearchIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { EntitySearchType } from '../../../__generated__/globalTypes';
import TransactionSlideout from '../../slideouts/TransactionSlideout';
import EntitySearchItem from '../EntitySearchItem';
import TransactionSearchItem from '../TransactionSearchItem';

import type { SearchEntitiesQuery, SearchEntitiesQueryVariables } from './__generated__/EntitySearchModal.gql';
import type { EntitySearchItemFragment } from '../EntitySearchItem/__generated__/EntitySearchItem.gql';
import type { TransactionSearchItemFragment } from '../TransactionSearchItem/__generated__/TransactionSearchItem.gql';

/** A result the modal can render — anything the SearchResult union returns. */
type SearchResultItem = EntitySearchItemFragment | TransactionSearchItemFragment;

// A predicate rather than an inline `__typename` check: codegen types the
// discriminant as optional, so only this narrows the non-transaction branch.
const isTransactionResult = (result: SearchResultItem): result is TransactionSearchItemFragment =>
  result.__typename === 'Transaction';

interface IBaseEntitySearchModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  entities: SearchResultItem[];
  query: string;
  onQueryChange: (query: string) => void;
  isSearchPending: boolean;
  onSelectTransaction: (transactionId: string) => void;
}

const BaseEntitySearchModal = ({
  isOpen,
  onOpenChange,
  entities,
  query,
  onQueryChange,
  isSearchPending,
  onSelectTransaction,
}: IBaseEntitySearchModalProps) => {
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const itemRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  React.useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, entities.length);
    if (entities.length === 0) {
      setSelectedIndex(-1);
      return;
    }
    if (selectedIndex >= entities.length) {
      setSelectedIndex(entities.length - 1);
    }
  }, [entities.length, selectedIndex]);

  React.useEffect(() => {
    setSelectedIndex(-1);
  }, [query, entities]);

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (entities.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prevSelectedIndex) =>
          prevSelectedIndex < 0 ? 0 : prevSelectedIndex === entities.length - 1 ? 0 : prevSelectedIndex + 1
        );
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prevSelectedIndex) =>
          prevSelectedIndex < 0
            ? entities.length - 1
            : prevSelectedIndex === 0
              ? entities.length - 1
              : prevSelectedIndex - 1
        );
        return;
      }

      if (event.key === 'Enter') {
        if (selectedIndex < 0) {
          return;
        }
        event.preventDefault();
        const selectedItem = itemRefs.current[selectedIndex]?.querySelector('[data-entity-search-item]');
        if (selectedItem instanceof HTMLElement) {
          selectedItem.click();
        }
      }
    },
    [entities.length, selectedIndex]
  );

  const hasMenu = entities.length > 0 || (query.trim() !== '' && !isSearchPending);
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="w-[650px]"
      overlayClassName="!items-start !pt-[15vh] sm:!items-start sm:!pt-[15vh]"
    >
      <div className="flex max-h-[60vh] w-full flex-col">
        <label className={cx('border-tertiary flex shrink-0 cursor-text items-center gap-2', hasMenu && 'border-b')}>
          <SmallSearchIcon className="text-quaternary ml-4 size-5 shrink-0" />
          <input
            type="text"
            autoFocus
            className="text-primary placeholder:text-placeholder h-13 w-full bg-transparent py-3 pr-4 text-sm outline-none"
            placeholder="Search merchants, accounts, or amounts..."
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
        </label>
        {entities.length > 0 ? (
          <div className="min-h-0 overflow-hidden overflow-y-auto p-1">
            {entities.map((entity, index) => (
              <div
                key={`${entity.__typename}-${entity.id}`}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
              >
                {isTransactionResult(entity) ? (
                  <TransactionSearchItem
                    transaction={entity}
                    isSelected={index === selectedIndex}
                    onSelect={() => onSelectTransaction(entity.id)}
                  />
                ) : (
                  <EntitySearchItem
                    entity={entity}
                    isSelected={index === selectedIndex}
                    onSelect={() => onOpenChange(false)}
                  />
                )}
              </div>
            ))}
          </div>
        ) : query.trim() !== '' && !isSearchPending ? (
          <div className="text-secondary h-13 flex items-center px-4 py-3 text-sm">No results found</div>
        ) : null}
      </div>
    </Modal>
  );
};

const SEARCH_ENTITIES = gql`
  query SearchEntities($input: EntitySearchInput!) {
    searchEntities(input: $input) {
      ...EntitySearchItem
      ...TransactionSearchItem
    }
  }
  ${EntitySearchItem.Fragment}
  ${TransactionSearchItem.Fragment}
`;

interface IEntitySearchModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const DEBOUNCE_MS = 250;

const EntitySearchModal = ({ isOpen, onOpenChange }: IEntitySearchModalProps) => {
  const [query, setQuery] = React.useState('');
  const [entities, setEntities] = React.useState<SearchResultItem[]>([]);
  const [isDebouncePending, setIsDebouncePending] = React.useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = React.useState(false);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const requestIdRef = React.useRef(0);

  const [searchEntities] = useLazyQuery<SearchEntitiesQuery, SearchEntitiesQueryVariables>(SEARCH_ENTITIES);

  React.useEffect(() => {
    if (!isOpen) {
      requestIdRef.current += 1;
      setQuery('');
      setEntities([]);
      clearTimeout(debounceTimerRef.current);
      setIsDebouncePending(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleQueryChange = React.useCallback(
    (newQuery: string) => {
      setQuery(newQuery);
      clearTimeout(debounceTimerRef.current);
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const nextQuery = newQuery.trim();

      if (nextQuery === '') {
        setEntities([]);
        setIsDebouncePending(false);
        return;
      }

      setIsDebouncePending(true);

      debounceTimerRef.current = setTimeout(() => {
        void (async () => {
          try {
            const result = await searchEntities({
              variables: {
                input: {
                  query: nextQuery,
                  types: [EntitySearchType.Merchant, EntitySearchType.FinancialAccount, EntitySearchType.Transaction],
                  limit: 10,
                },
              },
            });

            if (requestIdRef.current !== requestId) {
              return;
            }

            setEntities(result.data?.searchEntities ?? []);
          } catch {
            // A rejected search clears entities rather than stranding the previous
            // query's hits under a query that no longer matches them.
            if (requestIdRef.current === requestId) {
              setEntities([]);
            }
          } finally {
            if (requestIdRef.current === requestId) {
              setIsDebouncePending(false);
            }
          }
        })();
      }, DEBOUNCE_MS);
    },
    [searchEntities]
  );

  const handleSelectTransaction = React.useCallback(
    (transactionId: string) => {
      setSelectedTransactionId(transactionId);
      setIsSlideoutOpen(true);
      onOpenChange(false);
    },
    [onOpenChange]
  );

  return (
    <>
      <BaseEntitySearchModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        entities={entities}
        query={query}
        onQueryChange={handleQueryChange}
        isSearchPending={isDebouncePending}
        onSelectTransaction={handleSelectTransaction}
      />
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={isSlideoutOpen}
        onClose={() => setIsSlideoutOpen(false)}
      />
    </>
  );
};

export default EntitySearchModal;
export { BaseEntitySearchModal };
